import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableService,
} from '@web/shared/ui';
import { AdminProductCollectionItem } from '../../models/admin-product-collection-item';
import { AdminProductCollectionFilter } from '../../models/admin-product-collection-filter';
import { AdminProductListItemAction } from '../../enums/admin-product-list-item-action';
import { AdminProductListCollectionAction } from '../../enums/admin-product-list-collection-action';
import { AdminProductListAction } from '../../enums/admin-product-list-action';
import { AdminProductDataSourceService } from '../../services/admin-product-data-source.service';
import { Observable, of } from 'rxjs';
import { Route, RouteParam } from '@web/common';
import { once } from '@web/utils';
import { AdminProductListCustomCellTemplateComponent } from '../../components/admin-product-list-custom-cell-template/admin-product-list-custom-cell-template.component';
import { AdminProductStatusProviderService } from '../../providers/admin-product-status-provider.service';

@Component({
  selector: 'app-admin-product-list',
  imports: [TableComponent, AdminProductListCustomCellTemplateComponent],
  templateUrl: './admin-product-list.component.html',
  styleUrl: './admin-product-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminProductListComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminProductCollectionItem, AdminProductCollectionFilter, AdminProductListItemAction, AdminProductListCollectionAction, AdminProductListAction>).create();
  private readonly productDataSource = inject(AdminProductDataSourceService);
  /* prettier-ignore */
  private readonly productStatusProvider = inject(AdminProductStatusProviderService);
  private readonly table = inject(TableService);

  public readonly addVisibility = input<boolean>(false);

  protected config!: TableConfig<
    AdminProductCollectionItem,
    AdminProductCollectionFilter,
    AdminProductListItemAction,
    AdminProductListCollectionAction,
    AdminProductListAction
  >;

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .actions((builder) => [
        builder
          .initRouterLink(AdminProductListAction.Create)
          .label('Dodaj produkt')
          .visibility(() => of(this.addVisibility()))
          .routerLink((provider) => provider.routerLink(Route.ProductCreate))
          .build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminProductListItemAction.Preview)
          .visibility((item) => item.canBeViewed)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.ProductDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initRouterLink(AdminProductListItemAction.Edit)
          .visibility((item) => item.canBeEdited)
          .icon(Icon.Pencil)
          .tooltip('Edycja')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.ProductEdit, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initCommand(AdminProductListItemAction.Remove)
          .visibility((item) => item.canBeRemoved)
          .icon(Icon.Trash)
          .tooltip('Usuń')
          .command((item) => this.remove(item))
          .build(),
      ])
      .filters((builder) => [
        builder //
          .initText('slug')
          .build(),

        builder //
          .initText('cosmeticBrandName')
          .build(),

        builder //
          .initText('name')
          .build(),

        builder //
          .initSelect('status')
          .dictionary(() => this.productStatusProvider.selectItems())
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),
      ])
      .columns((builder) => [
        builder //
          .initCustom('featuredFileUrl')
          .width('9rem')
          .label('Zdjęcie główne')
          .build(),

        builder //
          .initText('name')
          .label('Nazwa')
          .sortable(true)
          .sortKey('name')
          .filterable(true)
          .filterKey('name')
          .build(),

        builder //
          .initText('cosmeticBrandName')
          .label('Nazwa marki')
          .sortable(true)
          .sortKey('cosmeticBrandName')
          .filterable(true)
          .filterKey('cosmeticBrandName')
          .build(),

        builder //
          .initNumber('availableQuantity')
          .label('Dostępna ilość')
          .sortable(true)
          .filterable(false)
          .sortKey('availableQuantity')
          .width('10rem')
          .build(),

        builder //
          .initCurrency('price')
          .label('Cena')
          .sortable(true)
          .sortKey('price')
          .filterable(false)
          .width('8rem')
          .build(),

        builder //
          .initDateTime('createdAt')
          .label('Data utworzenia')
          .sortable(true)
          .sortKey('createdAt')
          .filterable(true)
          .filterKey('createdAt')
          .width('12rem')
          .build(),

        builder //
          .initCustom('status')
          .filterKey('status')
          .filterable(true)
          .label('Status')
          .sortKey('status')
          .sortable(true)
          .width('12rem')
          .build(),

        builder //
          .initCustom('shopPreviewUrl')
          .width('20rem')
          .label('Adres URL do produktu')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminProductCollectionFilter>,
        ): Observable<TableDataPayload<AdminProductCollectionItem>> =>
          this.productDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private remove(item: AdminProductCollectionItem): void {
    this.productDataSource
      .removeById(item.id)
      .pipe(once())
      .subscribe(() => this.refresh());
  }

  private refresh(): void {
    this.table.refresh();
  }
}
